import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECTS, subjectStyle } from "@/lib/constants";
import { Plus, Trash2, X, Zap, RotateCcw, Check, Sparkles, Lock, Layers } from "lucide-react";
import { toast } from "sonner";

const BOX_LABELS = { 1: "Nouvelle", 2: "J+1", 3: "J+2", 4: "J+4", 5: "Maîtrisée" };

export default function Flashcards() {
  const { user } = useAuth();
  const isPremium = user?.plan === "premium";
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("review");
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sessionDone, setSessionDone] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "Mathématiques", front: "", back: "" });
  const [aiOpen, setAiOpen] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [sheetId, setSheetId] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = () =>
    api.get("/flashcards").then(({ data }) => {
      setCards(data);
      const due = data.filter((c) => c.due).sort(() => Math.random() - 0.5);
      setQueue(due);
      setIndex(0);
      setFlipped(false);
      setSessionDone(false);
    }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const current = queue[index];

  const answer = async (success) => {
    try {
      await api.post(`/flashcards/${current.id}/review`, { success });
      setFlipped(false);
      if (index + 1 < queue.length) {
        setTimeout(() => setIndex(index + 1), 150);
      } else {
        toast.success("Session terminée, bravo !");
        setSessionDone(true);
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/flashcards", form);
      toast.success("Carte créée");
      setOpen(false);
      setForm({ subject: "Mathématiques", front: "", back: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/flashcards/${id}`);
      toast.success("Carte supprimée");
      setCards(cards.filter((c) => c.id !== id));
      setQueue(queue.filter((c) => c.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAi = () => {
    if (!isPremium) {
      toast.info("Passe à Premium pour générer des flashcards par IA !");
      return;
    }
    api.get("/sheets").then(({ data }) => {
      setSheets(data);
      setSheetId(data[0]?.id || "");
    });
    setAiOpen(true);
  };

  const generateAi = async (e) => {
    e.preventDefault();
    if (!sheetId) {
      toast.error("Crée d'abord une fiche de révision !");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/generate-flashcards", { sheet_id: sheetId });
      toast.success(`${data.created} flashcards générées par l'IA !`);
      setAiOpen(false);
      load();
      setMode("review");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  const dueCount = cards.filter((c) => c.due).length;
  const mastered = cards.filter((c) => c.box === 5).length;

  return (
    <div className="space-y-6" data-testid="flashcards-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Flashcards</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1">
            Mémorise durablement grâce à la répétition espacée.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openAi}
            data-testid="ai-flashcards-button"
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-colors duration-200 ${
              isPremium ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90" : "bg-slate-100 text-slate-500"
            }`}
          >
            {isPremium ? <Sparkles className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            Générer depuis une fiche
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="add-flashcard-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-4 w-4" /> Nouvelle carte
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Cartes au total", value: cards.length, testid: "flashcards-total" },
          { label: "À réviser aujourd'hui", value: dueCount, testid: "flashcards-due" },
          { label: "Maîtrisées", value: mastered, testid: "flashcards-mastered" },
        ].map(({ label, value, testid }) => (
          <div key={testid} data-testid={testid} className="bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-5 shadow-sm text-center">
            <p className="text-xl sm:text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1.5 p-1 bg-white border border-slate-200 rounded-full w-fit" data-testid="flashcards-tabs">
        <button onClick={() => setMode("review")} data-testid="flashcards-tab-review" className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${mode === "review" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Réviser
        </button>
        <button onClick={() => setMode("manage")} data-testid="flashcards-tab-manage" className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${mode === "manage" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Mes cartes
        </button>
      </div>

      {mode === "review" && (
        <div data-testid="flashcards-review">
          {loading ? (
            <div className="animate-pulse h-64 bg-slate-100 rounded-3xl" />
          ) : queue.length === 0 || sessionDone ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80" data-testid="flashcards-empty">
              <Zap className="h-10 w-10 text-amber-400 mx-auto mb-3" />
              <p className="text-slate-700 font-semibold">Rien à réviser aujourd'hui, bravo !</p>
              <p className="text-sm text-slate-400 mt-1">Reviens demain — ou crée de nouvelles cartes.</p>
            </div>
          ) : current ? (
            <div className="max-w-xl mx-auto space-y-4">
              <p className="text-center text-xs font-semibold text-slate-500" data-testid="flashcards-progress">
                Carte {index + 1} / {queue.length}
              </p>
              <div
                onClick={() => setFlipped(!flipped)}
                data-testid="flashcard-flip"
                className="relative h-72 cursor-pointer"
                style={{ perspective: "1000px" }}
              >
                <div
                  className="absolute inset-0 transition-transform duration-500"
                  style={{ transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
                >
                  <div
                    className="absolute inset-0 rounded-3xl bg-white border-2 border-indigo-200 shadow-lg flex flex-col items-center justify-center p-8 text-center"
                    style={{ backfaceVisibility: "hidden" }}
                  >
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border mb-4 ${subjectStyle(current.subject)}`}>{current.subject}</span>
                    <p className="font-display text-xl sm:text-2xl font-semibold text-slate-900" data-testid="flashcard-front">{current.front}</p>
                    <p className="mt-6 text-xs text-slate-400 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Clique pour retourner</p>
                  </div>
                  <div
                    className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-600 shadow-lg flex flex-col items-center justify-center p-8 text-center"
                    style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                  >
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20 text-white mb-4">{current.subject}</span>
                    <p className="font-display text-lg sm:text-xl font-semibold text-white leading-relaxed" data-testid="flashcard-back">{current.back}</p>
                  </div>
                </div>
              </div>
              {flipped && (
                <div className="grid grid-cols-2 gap-3 animate-fade-in-up">
                  <button onClick={() => answer(false)} data-testid="flashcard-fail-button" className="py-3.5 rounded-full bg-rose-100 text-rose-700 font-semibold text-sm hover:bg-rose-200 transition-colors duration-200 flex items-center justify-center gap-2">
                    <X className="h-4 w-4" /> Je ne savais pas
                  </button>
                  <button onClick={() => answer(true)} data-testid="flashcard-success-button" className="py-3.5 rounded-full bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-600 transition-colors duration-200 flex items-center justify-center gap-2">
                    <Check className="h-4 w-4" /> Je savais !
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {mode === "manage" && (
        <div data-testid="flashcards-manage">
          {cards.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200/80">
              <Layers className="h-10 w-10 text-indigo-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Aucune carte pour l'instant.</p>
              <p className="text-sm text-slate-400 mt-1">Crée ta première carte ou génère-en depuis une fiche (Premium).</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {cards.map((c) => (
                <li key={c.id} data-testid={`flashcard-item-${c.id}`} className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center gap-4 shadow-sm">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${subjectStyle(c.subject)}`}>{c.subject}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.front}</p>
                    <p className="text-xs text-slate-500 truncate">{c.back}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${c.box === 5 ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`} data-testid={`flashcard-box-${c.id}`}>
                    {BOX_LABELS[c.box] || "Nouvelle"}
                  </span>
                  <button onClick={() => remove(c.id)} data-testid={`flashcard-delete-${c.id}`} className="p-2 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="flashcard-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900">Nouvelle flashcard</h2>
              <button onClick={() => setOpen(false)} data-testid="flashcard-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="flashcard-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Recto (question)</label>
                <textarea required rows={2} value={form.front} onChange={(e) => setForm({ ...form, front: e.target.value })} data-testid="flashcard-front-input" placeholder="Ex : Quelle est la dérivée de eˣ ?" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm resize-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Verso (réponse)</label>
                <textarea required rows={2} value={form.back} onChange={(e) => setForm({ ...form, back: e.target.value })} data-testid="flashcard-back-input" placeholder="Ex : (eˣ)' = eˣ" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm resize-none" />
              </div>
              <button type="submit" data-testid="flashcard-submit-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                Créer la carte
              </button>
            </form>
          </div>
        </div>
      )}

      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="ai-flashcards-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" /> Flashcards par IA
              </h2>
              <button onClick={() => setAiOpen(false)} data-testid="ai-flashcards-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">L'IA lit ta fiche et crée 8 cartes question/réponse automatiquement.</p>
            <form onSubmit={generateAi} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Fiche source</label>
                <select value={sheetId} onChange={(e) => setSheetId(e.target.value)} data-testid="ai-flashcards-sheet-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {sheets.map((s) => <option key={s.id} value={s.id}>{`${s.subject} — ${s.title}`}</option>)}
                </select>
              </div>
              <button type="submit" disabled={aiLoading} data-testid="ai-flashcards-submit" className="w-full py-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60">
                {aiLoading ? "Génération en cours… (10-20 s)" : "Générer 8 cartes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
